import React from 'react';
import { t } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { RQ_SCHEDULED_DEPLOYMENT } from 'Utils';
import { getScheduledDeployment } from 'Services';
import type { ReactQueryError } from '@brighterion/types/react-query';
import { shouldShowScheduledDeployment } from 'Selectors';
import { useNotificationsStore } from 'Hooks/zustand/useNotificationsStore';
import type { Status } from 'Hooks/zustand/useNotificationsStore';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useAcknowledgeScheduledDeployment from './useAcknowledgeScheduledDeployment';

export type ScheduledDeployment = {
    comment: string;
    createdAt: string;
    createdBy: string;
    executionOrderChange: boolean;
    globalRules: Changes;
    groups: Groups;
    id: number;
    merchants: Changes;
    scheduledTime: Date;
    status: NonNullable<Status>;
    tenantGroups: Changes;
    totalUpdates: number;
    userInfo: UserInfo;
    version: number;
    parameters: Parameters;
};

type Changes = {
    added: number;
    modified: number;
    removed: number;
    disabled?: number;
};

type Groups = {
    added: number;
    removed: number;
    modified?: number;
};

type Parameters = {
    added: number;
    removed: number;
};

type UserInfo = {
    id: string;
    firstName: string;
    lastName: string;
    tenant: string;
};

type ScheduledDeploymentWithSummary = {
    summary: ScheduledDeploymentSummary['current'];
} & ScheduledDeployment;

type ParseScheduledDeployment = {
    scheduledDeployment: ScheduledDeployment;
};

const parseScheduledDeployment = ({ scheduledDeployment }: ParseScheduledDeployment) => {
    const scheduledDeploymentWithSummary: ScheduledDeploymentWithSummary = {
        ...scheduledDeployment,
        summary: {
            groupsSummary: {
                addedGroups: scheduledDeployment.groups.added,
                removedGroups: scheduledDeployment.groups.removed,
                modifiedGroups: scheduledDeployment.groups.modified
            },
            acquirerSummary: {
                addedRules: scheduledDeployment.globalRules.added,
                removedRules: scheduledDeployment.globalRules.removed,
                modifiedRules: scheduledDeployment.globalRules.modified,
                configUpdated: false,
                modifiedExecutionOrder: scheduledDeployment.executionOrderChange
            },
            merchantSummary: {
                added: scheduledDeployment.merchants.added,
                removed: scheduledDeployment.merchants.removed,
                modified: scheduledDeployment.merchants.modified,
                disabled: scheduledDeployment.merchants.disabled ?? 0
            },
            tenantGroupSummary: {
                added: scheduledDeployment.tenantGroups.added,
                removed: scheduledDeployment.tenantGroups.removed,
                modified: scheduledDeployment.tenantGroups.modified
            },
            parameterSummary: {
                added: scheduledDeployment.parameters.added,
                removed: scheduledDeployment.parameters.removed
            },
            totalUpdates: scheduledDeployment.totalUpdates
        }
    };
    return scheduledDeploymentWithSummary;
};

type UseGetScheduledDeployment = {
    options: {
        polling: boolean;
    };
};

const useGetScheduledDeployment = ({ options }: UseGetScheduledDeployment) => {
    const [seconds, setSeconds] = React.useState(60);
    const { addAlert } = useAlertBanner();

    const setStatus = useNotificationsStore((state) => state.setStatus);

    const acknowledgeScheduledDeployment = useAcknowledgeScheduledDeployment();

    const handleAcknowledgeScheduledDeployment = () => {
        acknowledgeScheduledDeployment.mutate();
    };

    return useQuery<ScheduledDeploymentWithSummary, ReactQueryError>({
        queryKey: [RQ_SCHEDULED_DEPLOYMENT],
        queryFn: async () => {
            const data = await getScheduledDeployment();
            if (!data) {
                return data;
            }
            return parseScheduledDeployment({ scheduledDeployment: data });
        },
        enabled: shouldShowScheduledDeployment() && options.polling,
        staleTime: 1000,
        retry: (failureCount, error) => {
            if (failureCount > 5) {
                error.statusText = 'offline';
                return false;
            }
            return true;
        },
        refetchOnReconnect: true,
        refetchInterval: seconds * 1000,
        onSuccess: async (data) => {
            switch (data?.status) {
                case 'done': {
                    handleAcknowledgeScheduledDeployment();
                    setSeconds(60);
                    setStatus(data?.status);
                    const version = data?.version || 0;
                    addAlert({
                        id: version,
                        content: t('scheduledDeployment.finished.content', { version }),
                        status: 'success'
                    });
                    break;
                }
                case 'in_progress':
                case 'scheduled': {
                    setStatus(data?.status);
                    setSeconds(10);
                    break;
                }
                default: {
                    setStatus(undefined);
                    break;
                }
            }
        }
    });
};

export default useGetScheduledDeployment;

export const useGetScheduledDeploymentManually = () => {
    return useQuery<ScheduledDeploymentWithSummary, ReactQueryError>({
        queryKey: [RQ_SCHEDULED_DEPLOYMENT],
        queryFn: async () => {
            const data = await getScheduledDeployment();
            if (!data) {
                return data;
            }
            return parseScheduledDeployment({ scheduledDeployment: data });
        },
        enabled: shouldShowScheduledDeployment()
    });
};
